// "A new version is available" — the strip that turns a surprise into a choice.
//
// WHY IT SITS AT THE TOP OF EVERY PAGE. There is already a stale-bundle signal
// (M46): a dot on the What's-new nav item and a card on /changelog. Both are
// good, and neither reaches the person who is about to click "Purchases" on a
// tab that has been open since before the deploy. That click is the one that
// used to produce "Failed to fetch dynamically imported module", so the notice
// has to live where they already are.
//
// IT IS NOT THE FIX, IT IS THE COURTESY. `lazyRoute.ts` guarantees the app
// recovers whether or not anyone reads this. What this buys is that the reload
// is usually something the user chose a moment earlier, rather than something
// that happened to them mid-click — and it covers the tab that sits open for a
// day without navigating anywhere at all.
//
// DISMISSIBLE, AND IT MEANS IT. Someone in the middle of typing should be able
// to make it go away, and it must not reappear five minutes later out of the
// same deploy. It comes back only for a NEWER version than the one dismissed,
// which is a different fact and worth saying again.
//
// The shape copies `NetworkStatusBanner` deliberately — square, full-bleed,
// collapsing — because it belongs to the same family of app-level status
// strips, and a second visual language for the same job is how a shell starts
// looking assembled from parts.

import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import NewReleasesOutlined from '@mui/icons-material/NewReleasesOutlined';
import { useStaleBundle } from './useStaleBundle';

export default function UpdateAvailableBar() {
  const { deployedVersion } = useStaleBundle();
  const [dismissed, setDismissed] = useState<string | null>(null);

  // A newer deploy than the one that was waved away is news again.
  useEffect(() => {
    if (deployedVersion && dismissed && deployedVersion !== dismissed) setDismissed(null);
  }, [deployedVersion, dismissed]);

  const show = !!deployedVersion && deployedVersion !== dismissed;

  // `unmountOnExit` because this is a `role="alert"`: a collapsed-but-mounted
  // alert is a live region sitting in the tree waiting to announce itself, and
  // "not shown" should mean not present. It also makes the dismissed state
  // assertable as genuine absence rather than as zero height.
  return (
    <Collapse in={show} unmountOnExit>
      <Alert
        severity="info"
        icon={<NewReleasesOutlined fontSize="inherit" />}
        square
        sx={{ borderRadius: 0 }}
        // BOTH controls live in `action`. MUI's Alert renders its own close
        // button only when `onClose` is set and `action` is NOT — passing both
        // silently drops the ×, which is a bug this codebase has already
        // shipped once (see SuggestionNudge).
        action={(
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              color="inherit"
              size="small"
              sx={{ minHeight: 44 }}
              onClick={() => window.location.reload()}
            >
              Update
            </Button>
            <IconButton
              color="inherit"
              size="small"
              aria-label="Dismiss update notice"
              onClick={() => setDismissed(deployedVersion)}
            >
              <CloseOutlined fontSize="small" />
            </IconButton>
          </Box>
        )}
      >
        {`A new version is available. Reload to update to v${deployedVersion ?? ''}.`}
      </Alert>
    </Collapse>
  );
}
